import React, { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import { Table } from "react-bootstrap";
import html2canvas from "html2canvas";
import { useParams, useSearchParams } from "react-router";
import useGetAllBooking from "../../../../services/booking/hooks/useGetAllBooking";
import { format } from "date-fns";

const GuestDetailViewComponent: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const { data: booking, isFetching, isSuccess } = useGetAllBooking({
        filters: {
            invoice_number: id
        }
    });

    const [searchParams] = useSearchParams();
    const pdfRef = useRef<HTMLDivElement>(null);

    const isDownloadAction = searchParams.has("download"); // Check if 'download' is in URL

    const generatePDF = React.useCallback(async () => {
        const input = pdfRef.current;
        if (!input) return;

        const canvas = await html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
        });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`Guest_Detail-${id}.pdf`);
    }, [id]);

    useEffect(() => {
        if (!isFetching && booking && isDownloadAction) {
            console.log("Download action detected");
            generatePDF();
        }
    }, [isFetching, booking, isDownloadAction, generatePDF]);

    if (!id) {
        return (
            <h4 className="text-primary text-center fw-bold">Invoice not found</h4>
        );
    }

    if (!isFetching && booking && booking.data.length === 0) {
        return (
            <h4 className="text-primary text-center fw-bold">Invoice not found</h4>
        );
    }

    return (
        <>
            {isFetching && <h4 className="text-primary text-center fw-bold">Loading...</h4>}
            {isSuccess && booking && booking.data.length > 0 && (
                <div className="bg-white min-vh-100">
                    <div className="mt-3 bg-white p-3" ref={pdfRef} style={{ width: "210mm", margin: "auto" }}>
                        {booking.data.map((item, index) => (
                            <div key={item.id} style={{ pageBreakAfter: index === booking.data.length - 1 ? "auto" : "always", minHeight: "297mm", padding: "10mm" }}>
                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <ul className="list-unstyled text-start pe-4">
                                        <li className="fw-bold fs-3">{item?.property?.name || "N/A"}</li>
                                        <li className="fs-4">
                                            {[
                                                item?.property?.street_address,
                                                item?.property?.state,
                                                item?.property?.city,
                                                item?.property?.country,
                                            ]
                                                .filter(Boolean)
                                                .join(", ") || "N/A"}
                                        </li>
                                        <li className="fs-4">Phone: <span className="fw-semibold">{item?.property?.primary_phone_country_code || ""} {item?.property?.primary_phone || "N/A"}</span></li>
                                        <li className="fs-4">Email: <span className="fw-semibold">{item?.property?.primary_email || "N/A"}</span></li>
                                    </ul>
                                    <span className="fw-bold fs-2">{item?.property?.name || "N/A"}</span>
                                </div>

                                {/* Guest Detail */}
                                <div className="mt-3 p-2 border-top border-dark border-3 border-bottom">
                                    <h3 className="fs-2 pb-0 mb-0 text-center">Registration Form</h3>
                                </div>

                                <div className="container px-6">
                                    <Table bordered className="mt-3" style={{ tableLayout: 'fixed', width: '100%' }}>
                                         <thead>
                                             <tr>
                                                 <th className="uppercase text-center" colSpan={4}>Reservation Details</th>
                                             </tr>
                                         </thead>
                                         <tbody>
                                             <tr>
                                                 <td className="fw-bold" style={{ width: '20%' }}>Name of Guest</td>
                                                 <td className="uppercase" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', overflow: 'hidden' }}>{`${item?.customer_name || "N/A"} ${item?.customer_surname || ""}`.trim()}</td>
                                                 <td className="fw-bold" style={{ width: '20%' }}>Invoice No.</td>
                                                 <td style={{ wordBreak: 'break-word' }}>{item?.invoice_number || "N/A"}</td>
                                             </tr>
                                             <tr>
                                                 <td className="fw-bold">Agent</td>
                                                 <td style={{ wordBreak: 'break-word' }}>{item?.agent_name || "N/A"}</td>
                                                 <td className="fw-bold">Booking Date</td>
                                                 <td>{item?.invoice_date ? format(new Date(item?.invoice_date), 'dd MMMM yyyy') : "N/A"}</td>
                                             </tr>
                                             <tr>
                                                 <td className="fw-bold">Check-in</td>
                                                 <td>{item?.checkin_date ? format(new Date(item?.checkin_date), 'dd MMMM yyyy') : "N/A"}</td>
                                                 <td className="fw-bold">Check-out</td>
                                                 <td>{item?.checkout_date ? format(new Date(item?.checkout_date), 'dd MMMM yyyy') : "N/A"}</td>
                                             </tr>
                                             <tr>
                                                 <td className="fw-bold">Address</td>
                                                 <td style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', overflow: 'hidden' }}>
                                                     {[
                                                         item?.customer_address,
                                                         item?.customer_state,
                                                         item?.customer_city,
                                                         item?.customer_country,
                                                     ]
                                                         .filter(Boolean)
                                                         .join(", ") || "N/A"}
                                                 </td>
                                                 <td className="fw-bold">Booking Status</td>
                                                 <td>{item?.status || "N/A"}</td>
                                             </tr>
                                             <tr>
                                                 <td className="fw-bold">Phone</td>
                                                 <td style={{ wordBreak: 'break-word' }}>{item?.customer_phone || "N/A"}</td>
                                                 <td className="fw-bold">Email</td>
                                                 <td style={{ wordBreak: 'break-word' }}>{item?.customer_email || "N/A"}</td>
                                             </tr>
                                         </tbody>
                                     </Table>
                                </div>

                                <div className="px-4 mt-3">
                                    <Table bordered style={{ tableLayout: 'fixed', width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th colSpan={8}>Guests Detail</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="fw-bold" style={{ width: '10%' }}>Name</td>
                                                 <td className="uppercase" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', width: '20%', overflow: 'hidden' }}>
                                                     {item?.customer_name || "N/A"} {item?.customer_surname || ""}
                                                 </td>
                                                <td className="fw-bold" style={{ width: '5%' }}>ID</td>
                                                <td style={{ wordBreak: 'break-word', width: '15%' }}>{item?.customer_identity_number || "N/A"}</td>
                                                <td className="fw-bold" style={{ width: '10%' }}>Nationality</td>
                                                <td style={{ wordBreak: 'break-word', width: '15%' }}>{item?.customer_country || "N/A"}</td>
                                                <td className="fw-bold" style={{ width: '10%' }}>Contact</td>
                                                <td style={{ wordBreak: 'break-word', width: '15%' }}>
                                                    <p className="mb-0">{item?.customer_phone || "N/A"}</p>
                                                    <p className="mb-0">{item?.customer_email || "N/A"}</p>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Footer */}
                                 <div className="mt-5 pt-5 px-4 row container mx-auto" style={{ maxWidth: "700px", pageBreakInside: 'avoid' }}>
                                    <div className="col-6 mb-3 px-6">
                                        <span className="fw-semibold fs-2 d-block mb-1 text-center">Issued By,</span>
                                        <div style={{ height: "100px" }}></div>
                                        <span className="border-bottom border-dark border-2 d-block w-100 pb-1"></span>
                                    </div>
                                    <div className="col-6 mb-3 px-6">
                                        <span className="fw-semibold fs-2 d-block mb-1 text-center">Received By,</span>
                                        <div style={{ height: "100px" }}></div>
                                        <span className="border-bottom border-dark border-2 d-block w-100 pb-1"></span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default GuestDetailViewComponent;